import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TablePagination,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { getAllPricing, getPricingStats } from "../../../api/services/api";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../../components/DrawerTemplate";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader";
import { RoleGuard } from "../../../components/RBAC";
import { BulkPricingForm } from "./BulkPricingForm";
import { ClinicianPricingRow } from "./ClinicianPricingRow";
import { ServicePricingEditForm } from "./ServicePricingEditForm";
import { ClinicianGlobalUpdateForm } from "./ClinicianGlobalUpdateForm";

export const PricingManagement = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [globalUpdateDrawerOpen, setGlobalUpdateDrawerOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<{
    clinicianId: number;
    serviceId: number;
    mode: string;
    config?: any;
  } | null>(null);
  const [selectedClinician, setSelectedClinician] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const { data, isLoading } = useQuery(
    [
      "pricing",
      {
        search: search,
        limit: limit,
        offset: limit * page,
      },
    ],
    getAllPricing
  );

  const { data: statsData } = useQuery("pricing-stats", getPricingStats);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditService = (
    clinicianId: number,
    serviceId: number,
    mode: string,
    config: any
  ) => {
    setSelectedPricing({ clinicianId, serviceId, mode, config });
    setEditDrawerOpen(true);
  };

  const handleGlobalUpdate = (clinicianId: number, clinicianName: string) => {
    setSelectedClinician({ id: clinicianId, name: clinicianName });
    setGlobalUpdateDrawerOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "SAR",
    }).format(amount);
  };

  // Destructure clinicians and pagination from the API response (already unwrapped)
  const clinicians = data?.data?.clinicians || [];
  const paginationData = data?.data?.pagination;

  return (
    <Box my={2}>
      <Box
        mb={2}
        display="flex"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h3">Pricing Management</Typography>
        <Box display="flex" gap={2}>
          <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
            <Button variant="outlined" onClick={() => setBulkOpen(true)}>
              Bulk Update
            </Button>
          </RoleGuard>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statsData?.data && (
        <Box display="flex" gap={2} mb={3}>
          <Box
            sx={{
              padding: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              flex: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Total Pricing Configurations
            </Typography>
            <Typography variant="h4">
              {statsData.data.counts?.total || 0}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              flex: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Active Configurations
            </Typography>
            <Typography variant="h4">
              {statsData.data.counts?.active || 0}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              flex: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Average Price
            </Typography>
            <Typography variant="h4">
              {formatCurrency(statsData.data.priceRanges?.average || 0)}
            </Typography>
          </Box>
        </Box>
      )}

      <Box>
        <TextField
          label="Search by clinician or service"
          sx={{ width: "450px", padding: "0px" }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <Box mt={2}>
        {isLoading ? (
          <Loader />
        ) : clinicians.length === 0 ? (
          <EmptyScreen type="pricing" />
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Clinician</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Experience</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell>Pricing Configs</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clinicians.map((clinician: any) => (
                    <ClinicianPricingRow
                      key={clinician.clinician.id}
                      clinician={clinician}
                      onEditService={handleEditService}
                      onGlobalUpdate={handleGlobalUpdate}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                marginTop: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TablePagination
                count={paginationData?.total || 0}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={limit}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Service Pricing Edit Drawer */}
      <Drawer
        anchor="right"
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
      >
        <StyledDrawerHeader>
          <Typography>Edit Service Pricing</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => setEditDrawerOpen(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          {selectedPricing && (
            <ServicePricingEditForm
              clinicianId={selectedPricing.clinicianId}
              serviceId={selectedPricing.serviceId}
              consultationMode={selectedPricing.mode}
              initialData={selectedPricing.config}
              onClose={() => setEditDrawerOpen(false)}
            />
          )}
        </StyledDrawerContent>
      </Drawer>

      {/* Clinician Global Update Drawer */}
      <Drawer
        anchor="right"
        open={globalUpdateDrawerOpen}
        onClose={() => setGlobalUpdateDrawerOpen(false)}
      >
        <StyledDrawerHeader>
          <Typography>Global Pricing Update</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => setGlobalUpdateDrawerOpen(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          {selectedClinician && (
            <ClinicianGlobalUpdateForm
              clinicianId={selectedClinician.id}
              clinicianName={selectedClinician.name}
              onClose={() => setGlobalUpdateDrawerOpen(false)}
            />
          )}
        </StyledDrawerContent>
      </Drawer>

      {/* Bulk Update Drawer */}
      <Drawer anchor="right" open={bulkOpen} onClose={() => setBulkOpen(false)}>
        <StyledDrawerHeader>
          <Typography>Bulk Update Pricing</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => setBulkOpen(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          <BulkPricingForm toggleDrawer={(e: any) => setBulkOpen(e)} />
        </StyledDrawerContent>
      </Drawer>
    </Box>
  );
};
